import { useTranslation } from "react-i18next";
import { FormButton } from "../components/_input/_button";
import { Link, useHistory } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import axios from "axios";
import crypto from 'crypto-js';
import Joi from "joi";
import { GetMessages, formValidate } from '../utils/validation';
import { toast } from "react-toastify";
import * as db from '../utils/localDB';
import config from '../configuration/config';

const OTPInput = ({ value, onChange, onComplete, error }) => {
    const inputs = useRef([]);
    const [otp, setOtp] = useState(['', '', '', '']);

    useEffect(() => {
        if ('OTPCredential' in window && !otp.some(v => v !== '')) {
            const ac = new AbortController();
            navigator.credentials.get({
                otp: { transport: ['sms'] },
                signal: ac.signal
            }).then(otpCode => {
                if (otpCode && otpCode.code) {
                    const code = otpCode.code.slice(0, 4).split('');
                    setOtp(code);
                    onChange(code.join(''));
                    onComplete(code.join(''));
                }
            }).catch(err => {
                console.log("Web OTP Error:", err);
            });
            return () => {
                ac.abort();
            };
        }
    }, [onChange, onComplete, otp]);

    const handleChange = (e, index) => {
        const val = e.target.value;

        // Handle multiple characters (auto-fill or fast typing)
        if (val.length > 1) {
            const digits = val.split('').filter(char => !isNaN(char) && char.trim() !== '').slice(0, 4);
            if (digits.length > 0) {
                const newOtp = [...otp];
                digits.forEach((digit, i) => {
                    if (index + i < 4) newOtp[index + i] = digit;
                });
                setOtp(newOtp);
                const stringOtp = newOtp.join('');
                onChange(stringOtp);

                // Focus the last filled or next empty
                const lastIdx = Math.min(index + digits.length - 1, 3);
                inputs.current[lastIdx].focus();

                if (newOtp.every(v => v !== '') && stringOtp.length === 4) {
                    onComplete(stringOtp);
                }
            }
            return;
        }

        if (val !== '' && isNaN(val)) return;

        const newOtp = [...otp];
        newOtp[index] = val.substring(val.length - 1);
        setOtp(newOtp);

        const stringOtp = newOtp.join('');
        onChange(stringOtp);

        if (val && index < 3) {
            inputs.current[index + 1].focus();
        }

        if (newOtp.every(v => v !== '') && stringOtp.length === 4) {
            onComplete(stringOtp);
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputs.current[index - 1].focus();
        }
    };

    const handlePaste = (e) => {
        const data = e.clipboardData.getData('text').slice(0, 4).split('');
        if (data.length === 4 && data.every(char => !isNaN(char))) {
            setOtp(data);
            onChange(data.join(''));
            onComplete(data.join(''));
        }
    };

    return (
        <div className="otp-container">
            <div className={`otp-inputs ${error ? 'has-error' : ''}`}>
                {otp.map((data, index) => (
                    <input
                        key={index}
                        type="text"
                        ref={el => inputs.current[index] = el}
                        value={data}
                        onChange={e => handleChange(e, index)}
                        onKeyDown={e => handleKeyDown(e, index)}
                        onPaste={handlePaste}
                        maxLength={index === 0 ? 4 : 1} // Allow first field to absorb full code for auto-fill
                        className="otp-field"
                        autoComplete={index === 0 ? "one-time-code" : "off"}
                        inputMode="numeric"
                    />
                ))}
            </div>
            {error && <div className="otp-error-text">{error}</div>}
        </div>
    );
};

export default function LoginValidation({ onTabChange, onLogin }) {
    const { t: trans } = useTranslation(["common", "form", "inputs", "model"]);
    const tabsList = [{
        tab: 1, title: 'sign-in'
    }, {
        tab: 2, title: 'sign-up'
    }, {
        tab: 3, title: 'reset-password'
    }];
    const [requestForm, setRequestForm] = useState({
        validationKey: '',
    });
    const history = useHistory();
    const schema = Joi.object({
        validationKey: Joi.string().length(4).required().messages(GetMessages())
    });
    const [error, setError] = useState({});

    const handleRequest = async (otpVal) => {
        const currentOtp = otpVal || requestForm.validationKey;

        const result = formValidate(schema, { validationKey: currentOtp }) ?? {};
        setError(result);

        if (Object.keys(result).length > 0) return;

        try {
            const secretKey = "secretKey";
            const secretKeyValue = localStorage.getItem(secretKey);
            if (secretKeyValue) {
                db.createDbRequest("UserDB", 1);
                axios.post(
                    `${config.controllURL}api/Administration/SignInValidate2`,
                    {
                        "AccessType": "Global",
                        "Access": String(secretKeyValue),
                        "ValidationKey": currentOtp
                    }
                ).then((response) => {
                    if (response.status === 200) {
                        const encodedData = crypto.AES.encrypt(JSON.stringify(response.data), config.cryptoKey).toString();
                        localStorage.setItem("userData", JSON.stringify({
                            email: response.data.email, phone: response.data.phone,
                            id: response.data.id.toString(), descriptionAr: response.data.descriptionAr, descriptionEn: response.data.descriptionEn
                            , logo: response.data.logo
                        }));
                        localStorage.setItem("userId", response.data.id.split("_")[1]);
                        localStorage.setItem("custId", response.data.custID);
                        db.proccessUserData({ uId: 1, data: encodedData });
                        db.getUserDataByID(1).onsuccess = (event) => {
                            const result = event.target.result;
                            if (result) {
                                localStorage.setItem("isAuthenticated", "true");
                                history.push('/user');
                                if (onLogin)
                                    onLogin();
                            }
                        };
                    }
                }).catch((err) => {
                    if (err.response && err.response.status === 400) {
                        toast.error(trans("common:invaild-validation-key"));
                    }
                });
            }
        } catch (err) {
            console.log(err);
        }
    };



    return (
        <div className='popup-content'>
            <p className='text-size-14 mb-3 border-section-right p-2 opacity-80' style={{ background: 'transparent' }}>
                {trans("common:reset-password-step-2-note")}
            </p>

            <div className="otp-wrapper mt-4">
                <OTPInput
                    error={error?.validationKey}
                    value={requestForm.validationKey}
                    onChange={(otp) => {
                        const val = { ...requestForm };
                        val.validationKey = otp;
                        setRequestForm(val);
                    }}
                    onComplete={(otp) => {
                        handleRequest(otp);
                    }}
                />
            </div>

            <div className="flex flex-col items-center nice-gap-1rem mt-10">
                <FormButton
                    isPrimary={true}
                    name={trans("form:next")}
                    onClick={() => {
                        handleRequest();
                    }}
                    classes={" px-10 py-5 text-20 border-sm"}
                />

                <Link className='text-size-14 mt-4 text-center text-block text-primary font-bold' onClick={() => onTabChange(tabsList[0])}>
                    {trans("form:main-page")}
                </Link>
            </div>
        </div>
    );
}

